'use client';

import { c as _c } from "react/compiler-runtime";
import { Portal } from '@ark-ui/react/portal';
import { Select as ArkSelect, createListCollection } from '@ark-ui/react/select';
import { css, cva, cx } from '@repo/panda/css';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { ReactNode } from 'react';
import React, { Children, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../ds/icon';
import type { MaterialSymbolName } from '../types/material-symbol-names';
import { TextInput } from './text-input';
import { Type } from './type';
const SelectContext = createContext<{
  variant: 'dark' | 'light';
  searchValue?: string;
  virtualized?: boolean;
  grid?: boolean;
  scrollToIndexRef?: React.RefObject<((index: number) => void) | null>;
}>({
  variant: 'dark',
  grid: false
});
const useSelectContext = () => {
  return useContext(SelectContext);
};

// Centralized search filtering function
const createSearchFilter = (searchValue: string | undefined) => {
  if (!searchValue) return () => true;
  const searchLower = searchValue.toLowerCase();
  return (item: {
    label: string;
  }, searchText?: string) => {
    const labelText = item.label.toLowerCase();
    const searchTextLower = searchText?.toLowerCase() || '';
    return labelText.includes(searchLower) || searchTextLower.includes(searchLower);
  };
};

// Hook to calculate available space and determine optimal positioning
const useAvailableSpace = (triggerRef, isOpen) => {
  const $ = _c(9);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [availableSpace, setAvailableSpace] = useState(t0);
  const [optimalPlacement, setOptimalPlacement] = useState("bottom-start");
  const [maxHeight, setMaxHeight] = useState(300);
  let t1;
  let t2;
  if ($[1] !== isOpen || $[2] !== triggerRef) {
    t1 = () => {
      if (!isOpen || !triggerRef.current) {
        return;
      }
      const calculateSpace = () => {
        const trigger = triggerRef.current;
        if (!trigger) {
          return;
        }
        const rect = trigger.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const spaceBelow = viewportHeight - rect.bottom - 16;
        const spaceAbove = rect.top - 16;
        const spaceRight = viewportWidth - rect.left - 16;
        const spaceLeft = rect.right - 16;
        let placement;
        if (spaceBelow >= 200 || spaceBelow > spaceAbove) {
          placement = spaceRight >= 200 ? "bottom-start" : "bottom-end";
        } else {
          placement = spaceRight >= 200 ? "top-start" : "top-end";
        }
        const availableHeight = placement.includes("bottom") ? spaceBelow : spaceAbove;
        const calculatedMaxHeight = Math.max(100, Math.min(availableHeight - 20, 400));
        setAvailableSpace({
          top: spaceAbove,
          bottom: spaceBelow,
          left: spaceLeft,
          right: spaceRight
        });
        setOptimalPlacement(placement);
        setMaxHeight(calculatedMaxHeight);
      };
      calculateSpace();
      const handleResize = () => calculateSpace();
      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", calculateSpace, true);
      return () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", calculateSpace, true);
      };
    };
    t2 = [isOpen, triggerRef];
    $[1] = isOpen;
    $[2] = triggerRef;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== availableSpace || $[6] !== maxHeight || $[7] !== optimalPlacement) {
    t3 = {
      availableSpace,
      optimalPlacement,
      maxHeight
    };
    $[5] = availableSpace;
    $[6] = maxHeight;
    $[7] = optimalPlacement;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
export interface SelectProps extends Omit<ArkSelect.RootProps<SelectItemProps['item']>, 'children' | 'onValueChange' | 'value' | 'collection'> {
  children: ReactNode;
  trigger?: ReactNode;
  valueContent?: ReactNode;
  leadingIcon?: MaterialSymbolName;
  fieldSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  helperText?: string;
  errorText?: string;
  label?: string;
  fullWidth?: boolean;
  className?: string;
  contentClassName?: string;
  size?: SelectVariants['size'];
  variant?: SelectVariants['variant'];
  offset?: number;
  closeOnSelect?: boolean;
  matchTriggerWidth?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  virtualized?: boolean;
  maxHeight?: number;
  itemHeight?: number;
  grid?: boolean;
  gridCols?: number;
  gridItemSize?: number;
  zIndex?: number;
  portalled?: boolean;
  placeholder?: string;
  value?: string | null;
}
export interface SelectItemProps extends Omit<ArkSelect.ItemProps, 'children' | 'className'> {
  children: ReactNode;
  className?: string;
  variant?: 'dark' | 'light';
  leadingIcon?: MaterialSymbolName;
  trailingIcon?: MaterialSymbolName;
  onSelect?: () => void;
  item: {
    label: string;
    value: string;
    disabled?: boolean;
  };
  searchText?: string;
}
export interface SelectSeparatorProps {
  className?: string;
  variant?: 'dark' | 'light';
}
export interface SelectGroupProps extends Omit<ArkSelect.ItemGroupProps, 'children' | 'className'> {
  children: ReactNode;
  label?: string;
  className?: string;
  variant?: 'dark' | 'light';
}
export interface GridSelectItemProps extends Omit<ArkSelect.ItemProps, 'children' | 'className'> {
  children: ReactNode;
  className?: string;
  variant?: 'dark' | 'light';
  onSelect?: () => void;
  item: {
    label: string;
    value: string;
    disabled?: boolean;
  };
  searchText?: string;
  size?: number;
}
const collectSelectItems = (children: ReactNode): React.ReactElement<SelectItemProps | GridSelectItemProps>[] => Children.toArray(children).flatMap(child => {
  if (React.isValidElement<SelectGroupProps>(child) && (child.type === SelectGroup || child.type === React.Fragment)) return collectSelectItems(child.props.children);
  if (React.isValidElement<SelectItemProps | GridSelectItemProps>(child) && (child.type === SelectItem || child.type === GridSelectItem)) return [child];
  return [];
});
const selectContentStyles = cva({
  base: {
    outline: 'none',
    borderRadius: '16',
    boxShadow: '[0 25px 50px token(colors.scrim.default)]',
    padding: '8',
    minWidth: '[200px]',
    textStyle: 'headerXs',
    backdropFilter: '[blur(12.5px)]',
    overflow: 'hidden',
    gap: '4',
    display: 'flex',
    flexDirection: 'column',
    '& [data-part="item"] + [data-part="item"]': {
      marginTop: '4'
    }
  },
  variants: {
    size: {
      sm: {
        width: '[180px]',
        minWidth: '[180px]',
        maxWidth: '[180px]'
      },
      md: {
        width: '[260px]',
        minWidth: '[260px]',
        maxWidth: '[260px]'
      },
      lg: {
        width: '[320px]',
        minWidth: '[320px]',
        maxWidth: '[320px]'
      }
    },
    variant: {
      dark: {
        background: '[linear-gradient(token(colors.scrim.default), token(colors.scrim.default)), linear-gradient(token(colors.fill.stronger), token(colors.fill.stronger))]',
        color: 'text.highlight'
      },
      light: {
        background: 'blackAlpha.5',
        color: 'wemod.black.80'
      }
    }
  },
  defaultVariants: {
    size: 'md',
    variant: 'dark'
  }
});
const selectFieldStyles = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    width: 'full',
    minWidth: '0',
    gap: '8',
    paddingX: '12',
    borderRadius: '12',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    backgroundColor: 'fill.default',
    color: 'text.primary',
    textStyle: 'bodyS',
    textAlign: 'left',
    cursor: 'pointer',
    outline: 'none',
    '--select-icon-size': 'token(sizes.icon.sm)',
    _hover: {
      backgroundColor: 'fill.stronger'
    },
    _focus: {
      backgroundColor: 'fill.stronger',
      borderColor: 'fill.heavy'
    },
    _open: {
      backgroundColor: 'fill.stronger',
      borderColor: 'fill.heavy'
    },
    _disabled: {
      cursor: 'not-allowed',
      _hover: {
        backgroundColor: 'fill.default'
      }
    },
    _invalid: {
      backgroundColor: 'status.error.surface',
      borderColor: 'status.error',
      _hover: {
        backgroundColor: 'status.error.surface'
      },
      _focus: {
        backgroundColor: 'status.error.surface',
        borderColor: 'status.error'
      },
      _open: {
        backgroundColor: 'status.error.surface',
        borderColor: 'status.error'
      }
    }
  },
  variants: {
    size: {
      xs: {
        height: 'control.xs',
        paddingX: '8',
        textStyle: 'bodyXs'
      },
      sm: {
        height: 'control.sm'
      },
      md: {
        height: 'control.md'
      },
      lg: {
        height: 'control.lg',
        paddingX: '16',
        textStyle: 'bodyM',
        '--select-icon-size': 'token(sizes.icon.md)'
      },
      xl: {
        height: 'control.xl',
        paddingX: '20',
        gap: '12',
        textStyle: 'bodyL',
        '--select-icon-size': 'token(sizes.icon.inline.xl)'
      },
      '2xl': {
        height: 'control.2xl',
        paddingX: '24',
        gap: '12',
        textStyle: 'bodyL',
        '--select-icon-size': 'token(sizes.icon.lg)'
      }
    }
  },
  defaultVariants: {
    size: 'md'
  }
});
const selectItemStyles = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    padding: '12',
    gap: '12',
    borderRadius: '12',
    cursor: 'pointer',
    transition: 'all',
    textStyle: 'headerXs',
    background: 'transparent',
    border: 'none',
    width: 'full',
    textAlign: 'left',
    _disabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
      _hover: {
        background: 'transparent'
      }
    }
  },
  variants: {
    variant: {
      dark: {
        color: 'text.highlight',
        _hover: {
          background: 'whiteAlpha.10'
        },
        _focusVisible: {
          background: 'whiteAlpha.10',
          outline: 'none'
        },
        _highlighted: {
          background: 'whiteAlpha.10'
        },
        _checked: {
          background: 'fill.strong',
          _hover: {
            background: 'fill.strong'
          },
          _highlighted: {
            background: 'fill.strong'
          }
        }
      },
      light: {
        color: 'wemod.black.80',
        _hover: {
          background: 'blackAlpha.10'
        },
        _focusVisible: {
          background: 'blackAlpha.10',
          outline: 'none'
        },
        _highlighted: {
          background: 'blackAlpha.10'
        },
        _checked: {
          background: 'blackAlpha.15',
          _hover: {
            background: 'blackAlpha.15'
          },
          _highlighted: {
            background: 'blackAlpha.15'
          }
        }
      }
    }
  },
  defaultVariants: {
    variant: 'dark'
  }
});
const selectSeparatorStyles = cva({
  base: {
    height: '[1px]',
    marginTop: '8',
    marginBottom: '8',
    borderTopWidth: '1px',
    borderTopStyle: 'solid'
  },
  variants: {
    variant: {
      dark: {
        borderColor: 'whiteAlpha.10'
      },
      light: {
        borderColor: 'blackAlpha.10'
      }
    }
  },
  defaultVariants: {
    variant: 'dark'
  }
});
const selectGroupStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '0',
  '& + &': {
    paddingTop: '8',
    marginTop: '8',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: 'whiteAlpha.10'
  }
});
const selectGroupLabelStyles = cva({
  base: {
    padding: '[8px 12px]',
    textStyle: 'caption',
    textTransform: 'uppercase',
    letterSpacing: '[0.75px]',
    whiteSpace: 'nowrap'
  },
  variants: {
    variant: {
      dark: {
        color: 'text.secondary'
      },
      light: {
        color: 'wemod.black.60'
      }
    }
  },
  defaultVariants: {
    variant: 'dark'
  }
});
const noResultsStyles = css({
  padding: '[8px 12px]',
  fontSize: 'md',
  textAlign: 'center',
  fontStyle: 'italic'
});
const gridSelectItemStyles = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2',
    borderRadius: '8',
    cursor: 'pointer',
    transition: 'all',
    background: 'transparent',
    border: 'none',
    width: 'full',
    height: 'full',
    minHeight: '[40px]',
    _disabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  },
  variants: {
    variant: {
      dark: {
        color: 'text.primary',
        _hover: {
          background: 'whiteAlpha.10'
        },
        _focusVisible: {
          background: 'whiteAlpha.10',
          outline: 'none'
        },
        _highlighted: {
          background: 'whiteAlpha.10'
        }
      },
      light: {
        color: 'wemod.black.80',
        _hover: {
          background: 'blackAlpha.10'
        },
        _focusVisible: {
          background: 'blackAlpha.10',
          outline: 'none'
        },
        _highlighted: {
          background: 'blackAlpha.10'
        }
      }
    }
  },
  defaultVariants: {
    variant: 'dark'
  }
});
const gridContainerStyles = css({
  display: 'grid',
  gap: '2',
  padding: '2',
  maxHeight: 'auto',
  overflow: 'auto'
});
const virtualizedContainerStyles = css({
  overflow: 'auto',
  width: 'full'
});
export const GridSelectItem = t0 => {
  const $ = _c(21);
  let arkUIProps;
  let children;
  let className;
  let item;
  let searchText;
  let t1;
  let variant;
  if ($[0] !== t0) {
    ({
      children,
      className,
      variant,
      item,
      searchText,
      size: t1,
      ...arkUIProps
    } = t0);
    $[0] = t0;
    $[1] = arkUIProps;
    $[2] = children;
    $[3] = className;
    $[4] = item;
    $[5] = searchText;
    $[6] = t1;
    $[7] = variant;
  } else {
    arkUIProps = $[1];
    children = $[2];
    className = $[3];
    item = $[4];
    searchText = $[5];
    t1 = $[6];
    variant = $[7];
  }
  const size = t1 === undefined ? 40 : t1;
  const {
    variant: contextVariant,
    searchValue
  } = useSelectContext();
  const finalVariant = variant || contextVariant;
  const searchFilter = createSearchFilter(searchValue);
  let t2;
  bb0: {
    if (!item || typeof item.label !== "string" || typeof item.value !== "string") {
      t2 = false;
      break bb0;
    }
    t2 = searchFilter(item, searchText);
  }
  const isVisible = t2;
  if (!isVisible) {
    return null;
  }
  let t3;
  if ($[8] !== className || $[9] !== finalVariant) {
    t3 = cx(gridSelectItemStyles({
      variant: finalVariant
    }), className);
    $[8] = className;
    $[9] = finalVariant;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== size) {
    t4 = {
      minHeight: size,
      minWidth: size
    };
    $[11] = size;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== children) {
    t5 = <ArkSelect.ItemText>{children}</ArkSelect.ItemText>;
    $[13] = children;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== arkUIProps || $[16] !== item || $[17] !== t3 || $[18] !== t4 || $[19] !== t5) {
    t6 = <ArkSelect.Item className={t3} item={item} style={t4} role="option" aria-label={item.label} {...arkUIProps}>{t5}</ArkSelect.Item>;
    $[15] = arkUIProps;
    $[16] = item;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  return t6;
};

// Simple virtualization wrapper for regular select items
const VirtualizedSelectContent = ({
  children,
  maxHeight,
  itemHeight
}: {
  children: ReactNode;
  maxHeight: number;
  itemHeight: number;
}) => {
  // The React Compiler memoizes getVirtualItems() against the stable virtualizer instance,
  // freezing the list on scroll: https://github.com/TanStack/virtual/issues/736
  'use no memo';

  const parentRef = useRef<HTMLDivElement>(null);
  const {
    searchValue,
    scrollToIndexRef
  } = useSelectContext();
  const {
    sections: sections_0,
    rows: rows_0
  } = useMemo(() => {
    const searchFilter = createSearchFilter(searchValue);
    let offset = 0;
    let optionIndex = 0;
    const rows: {
      section: number;
      key: string;
      height: number;
      optionIndex?: number;
      item?: React.ReactElement<SelectItemProps | GridSelectItemProps>;
    }[] = [];
    const sections = Children.toArray(children).flatMap((child, section) => {
      const group = React.isValidElement<SelectGroupProps>(child) && child.type === SelectGroup ? child : undefined;
      const items = collectSelectItems(group ? group.props.children : child).filter(item => item.type === SelectItem && searchFilter(item.props.item, item.props.searchText));
      if (!items.length) return [];
      const start = offset;
      const separator = !!group && rows.length > 0;
      if (group) {
        const height = (group.props.label ? 32 : 0) + (separator ? 17 : 0);
        rows.push({
          section,
          key: `group-${group.key ?? section}`,
          height
        });
        offset += height;
      }
      items.forEach((item_0, index) => {
        const height_0 = itemHeight + (index < items.length - 1 || !group ? 4 : 0);
        rows.push({
          section,
          key: `item-${item_0.props.item.value}`,
          height: height_0,
          optionIndex: optionIndex++,
          item: item_0
        });
        offset += height_0;
      });
      return [{
        section,
        group,
        start,
        height: offset - start,
        separator
      }];
    });
    return {
      sections,
      rows
    };
  }, [children, searchValue, itemHeight]);
  const virtualizer = useVirtualizer({
    count: rows_0.length,
    getScrollElement: () => parentRef.current,
    estimateSize: index_0 => rows_0[index_0]?.height ?? itemHeight,
    getItemKey: index_1 => rows_0[index_1]?.key ?? index_1,
    overscan: 5
  });
  useEffect(() => {
    if (!scrollToIndexRef) return;
    scrollToIndexRef.current = index_2 => {
      const rowIndex = rows_0.findIndex(row => row.optionIndex === index_2);
      if (rowIndex >= 0) virtualizer.scrollToIndex(rowIndex, {
        align: 'auto'
      });
    };
    return () => {
      scrollToIndexRef.current = null;
    };
  }, [rows_0, scrollToIndexRef, virtualizer]);
  const totalHeight = virtualizer.getTotalSize();
  const virtualItems = virtualizer.getVirtualItems();
  return <div ref={parentRef} className={virtualizedContainerStyles} style={{
    height: Math.min(maxHeight, totalHeight)
  }}>
            <div style={{
      height: totalHeight,
      width: '100%',
      position: 'relative'
    }}>
                {sections_0.map(section_0 => {
        const visibleRows = virtualItems.filter(item_1 => rows_0[item_1.index]?.section === section_0.section);
        if (!visibleRows.length) return null;
        const content = visibleRows.map(virtualItem => {
          const row_0 = rows_0[virtualItem.index];
          if (!row_0?.item) return null;
          return <div key={row_0.key} style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: itemHeight,
            transform: `translateY(${virtualItem.start - (section_0.group ? section_0.start : 0)}px)`
          }}>
                                {row_0.item}
                            </div>;
        });
        return section_0.group ? <SelectGroup {...section_0.group.props} key={section_0.group.key ?? section_0.section} className={cx(section_0.group.props.className, section_0.separator && css({
          _before: {
            content: '""',
            position: 'absolute',
            top: '8',
            left: '0',
            right: '0',
            height: '[1px]',
            backgroundColor: 'fill.default'
          }
        }))} style={{
          ...section_0.group.props.style,
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: section_0.height,
          transform: `translateY(${section_0.start}px)`,
          marginTop: 0,
          paddingTop: section_0.separator ? 17 : 0,
          borderTopWidth: 0
        }}>
                            {content}
                        </SelectGroup> : <React.Fragment key={section_0.section}>{content}</React.Fragment>;
      })}
            </div>
        </div>;
};

// Simple virtualization wrapper for grid items
const VirtualizedGridContent = ({
  children,
  maxHeight,
  gridCols,
  gridItemSize
}: {
  children: ReactNode;
  maxHeight: number;
  gridCols: number;
  gridItemSize: number;
}) => {
  // The React Compiler memoizes getVirtualItems() against the stable virtualizer instance,
  // freezing the grid on scroll: https://github.com/TanStack/virtual/issues/736
  'use no memo';

  const parentRef = useRef<HTMLDivElement>(null);
  const {
    searchValue
  } = useSelectContext();

  // Convert children to array and filter out grid items
  const childrenArray = Children.toArray(children);
  const items = childrenArray.filter(child => React.isValidElement(child) && child.type === GridSelectItem);

  // Filter items based on search if needed
  const searchFilter = useMemo(() => createSearchFilter(searchValue), [searchValue]);
  const visibleItems = useMemo(() => {
    if (!searchValue) return items;
    return items.filter(item => {
      if (!React.isValidElement(item)) return false;
      const itemProps = item.props as GridSelectItemProps;
      return searchFilter(itemProps.item, itemProps.searchText);
    });
  }, [items, searchValue, searchFilter]);

  // Calculate grid dimensions with validation
  const itemsPerRow = Math.max(1, gridCols); // Ensure at least 1 column
  const rows = Math.ceil(visibleItems.length / itemsPerRow);
  const rowHeight = Math.max(20, gridItemSize) + 8; // Ensure minimum size
  const totalHeight = rows * rowHeight;
  const virtualizer = useVirtualizer({
    count: rows,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 2
  });
  const virtualRows = virtualizer.getVirtualItems();
  return <div ref={parentRef} className={virtualizedContainerStyles} style={{
    height: Math.min(maxHeight, totalHeight)
  }}>
            <div style={{
      height: `${totalHeight}px`,
      width: '100%',
      position: 'relative'
    }}>
                {virtualRows.map(virtualRow => {
        const rowIndex = virtualRow.index;
        const startIndex = rowIndex * itemsPerRow;
        const endIndex = Math.min(startIndex + itemsPerRow, visibleItems.length);
        const rowItems = visibleItems.slice(startIndex, endIndex);
        return <div key={`row-${virtualRow.index}`} style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: `${rowHeight}px`,
          transform: `translateY(${virtualRow.start}px)`,
          display: 'grid',
          gridTemplateColumns: `repeat(${itemsPerRow}, 1fr)`,
          gap: '8px',
          padding: '4px'
        }}>
                            {rowItems.map((item_0, index) => {
            if (!React.isValidElement(item_0)) return null;
            const itemProps_0 = item_0.props as GridSelectItemProps;
            const stableKey = itemProps_0.item?.value || `${rowIndex}-${index}`;
            return <div key={stableKey} style={{
              width: '100%',
              height: `${gridItemSize}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
                                        {item_0}
                                    </div>;
          })}
                        </div>;
      })}
            </div>
        </div>;
};
export type SelectVariants = NonNullable<Parameters<typeof selectContentStyles>[0]>;
export const Select = ({
  children,
  trigger,
  valueContent,
  leadingIcon,
  fieldSize = 'md',
  helperText,
  errorText,
  invalid = !!errorText,
  placeholder,
  'aria-label': ariaLabel,
  className,
  contentClassName,
  size = 'md',
  variant = 'dark',
  offset = 8,
  closeOnSelect = true,
  onOpenChange,
  disabled = false,
  matchTriggerWidth = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  onValueChange,
  virtualized = false,
  lazyMount = virtualized,
  unmountOnExit = virtualized,
  maxHeight = 300,
  itemHeight = 44,
  grid = false,
  gridCols = 4,
  gridItemSize = 100,
  zIndex = 9999,
  portalled = true,
  fullWidth = false,
  label = '',
  value,
  ...arkUIProps
}: SelectProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const descriptionId = React.useId();
  const scrollToIndexRef = useRef<((index: number) => void) | null>(null);

  // Use the available space hook to calculate optimal positioning
  const {
    optimalPlacement,
    maxHeight: calculatedMaxHeight
  } = useAvailableSpace(triggerRef as React.RefObject<HTMLElement | null>, isOpen);

  // Validate props to prevent runtime errors
  const validatedGridCols = Math.max(1, gridCols || 4);
  const validatedItemHeight = Math.max(20, itemHeight || 44);
  const validatedGridItemSize = Math.max(20, gridItemSize || 100);
  const validatedMaxHeight = Math.min(maxHeight, Math.max(100, calculatedMaxHeight || 300));

  // Create a default collection from children if none provided
  const defaultCollection = useMemo(() => {
    const searchFilter = createSearchFilter(searchValue);
    const collection = createListCollection<SelectItemProps['item']>({
      items: collectSelectItems(children).filter(child => searchFilter(child.props.item, child.props.searchText)).map(child_0 => child_0.props.item)
    });
    return collection;
  }, [children, searchValue]);
  const contentStyle = cx(selectContentStyles({
    size,
    variant
  }), contentClassName);
  const handleOpenChange = (details: ArkSelect.OpenChangeDetails) => {
    setIsOpen(details.open);
    if (!details.open) {
      setSearchValue('');
    } else if (searchable && searchInputRef.current) {
      // Focus search input when select opens if searchable
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
    onOpenChange?.(details);
  };

  // Handler to extract value and call user's onValueChange
  const handleValueChange = (details_0: ArkSelect.ValueChangeDetails) => {
    if (onValueChange) {
      const value_0 = Array.isArray(details_0?.value) ? details_0.value[0] ?? '' : details_0?.value ?? '';
      onValueChange(value_0);
    }
  };
  return <ArkSelect.Root onOpenChange={handleOpenChange} closeOnSelect={closeOnSelect} positioning={{
    placement: optimalPlacement,
    gutter: offset
  }} onValueChange={handleValueChange} collection={defaultCollection} scrollToIndexFn={virtualized && !grid ? ({
    index
  }: {
    index: number;
  }) => scrollToIndexRef.current?.(index) : undefined} disabled={disabled} invalid={invalid} lazyMount={lazyMount} unmountOnExit={unmountOnExit} {...arkUIProps} value={value === undefined ? undefined : value ? [value] : []} multiple={false} className={cx(className, css({
    display: 'flex',
    flexDirection: 'column',
    gap: '6',
    width: fullWidth ? 'full' : 'auto',
    opacity: disabled && !trigger ? 0.5 : undefined
  }))}>
            {label && <ArkSelect.Label className={css({
      textStyle: fieldSize === 'xs' ? 'header2xs' : 'caption',
      color: 'text.primary',
      textTransform: 'uppercase',
      letterSpacing: '[0.75px]'
    })}>
                    {label}
                </ArkSelect.Label>}
            <ArkSelect.Trigger ref={triggerRef} asChild={!!trigger} disabled={disabled} aria-label={ariaLabel} aria-describedby={errorText || helperText ? descriptionId : undefined} aria-errormessage={invalid && errorText ? descriptionId : undefined} className={cx(!trigger && selectFieldStyles({
      size: fieldSize
    }), className, css({
      width: fullWidth || !trigger ? 'full' : 'auto'
    }))}>
                {trigger ?? <>
                        <span className={css({
          display: 'flex',
          alignItems: 'center',
          gap: '8',
          minWidth: '0',
          flex: 'auto',
          '[data-placeholder-shown] &': {
            color: 'text.placeholder'
          },
          '& [data-part="value-text"]': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }
        })}>
                            {leadingIcon && <Icon icon={leadingIcon} size="sm" decorative />}
                            {valueContent ?? <ArkSelect.ValueText placeholder={placeholder} />}
                        </span>
                        <Icon icon="expand_more" decorative className={css({
          fontSize: 'var(--select-icon-size)',
          width: 'var(--select-icon-size)',
          height: 'var(--select-icon-size)'
        })} />
                    </>}
            </ArkSelect.Trigger>
            {arkUIProps.name && <ArkSelect.HiddenSelect />}
            <Portal disabled={!portalled}>
                <ArkSelect.Positioner style={{
        zIndex
      }}>
                    <ArkSelect.Content style={{
          minWidth: matchTriggerWidth ? `${triggerRef.current?.offsetWidth}px` : 'revert-layer',
          maxWidth: matchTriggerWidth ? `${triggerRef.current?.offsetWidth}px` : 'revert-layer',
          maxHeight: `${validatedMaxHeight}px`,
          overflow: 'auto'
        }} className={contentStyle} onClick={(e: React.MouseEvent) => {
          // Prevent clicks from bubbling up and closing the select
          e.stopPropagation();
        }} role="listbox" aria-label="Select options">
                        <SelectContext.Provider value={{
            variant,
            searchValue,
            virtualized,
            grid,
            scrollToIndexRef
          }}>
                            {searchable && <TextInput leadingIcon="search" ref={searchInputRef} size="sm" placeholder={searchPlaceholder} value={searchValue} onChange={e_0 => setSearchValue(e_0.target.value)} aria-label={`Search ${searchPlaceholder.toLowerCase()}`} fullWidth />}
                            {grid ? virtualized ? <VirtualizedGridContent maxHeight={validatedMaxHeight} gridCols={validatedGridCols} gridItemSize={validatedGridItemSize}>
                                        {children}
                                    </VirtualizedGridContent> : <div className={cx(gridContainerStyles, css({
              gridTemplateColumns: `repeat(${validatedGridCols}, 1fr)`,
              overflow: 'auto'
            }))} style={{
              maxHeight: `${validatedMaxHeight}px`
            }}>
                                        {children}
                                    </div> : virtualized ? <VirtualizedSelectContent key={searchValue} maxHeight={validatedMaxHeight} itemHeight={validatedItemHeight}>
                                    {children}
                                </VirtualizedSelectContent> : <div className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: '0'
            })} style={{
              maxHeight: `${validatedMaxHeight}px`,
              overflow: 'auto'
            }}>
                                    {children}
                                </div>}
                        </SelectContext.Provider>
                    </ArkSelect.Content>
                </ArkSelect.Positioner>
            </Portal>

            {(errorText || helperText) && <Type id={descriptionId} variant="body-xs" color={invalid && errorText ? 'status.error' : 'text.secondary'}>
                    {invalid && errorText ? errorText : helperText}
                </Type>}
        </ArkSelect.Root>;
};
export const SelectItem = t0 => {
  const $ = _c(41);
  let arkUIProps;
  let children;
  let className;
  let item;
  let leadingIcon;
  let searchText;
  let trailingIcon;
  let variant;
  if ($[0] !== t0) {
    ({
      children,
      className,
      variant,
      leadingIcon,
      trailingIcon,
      item,
      searchText,
      ...arkUIProps
    } = t0);
    $[0] = t0;
    $[1] = arkUIProps;
    $[2] = children;
    $[3] = className;
    $[4] = item;
    $[5] = leadingIcon;
    $[6] = searchText;
    $[7] = trailingIcon;
    $[8] = variant;
  } else {
    arkUIProps = $[1];
    children = $[2];
    className = $[3];
    item = $[4];
    leadingIcon = $[5];
    searchText = $[6];
    trailingIcon = $[7];
    variant = $[8];
  }
  const {
    variant: contextVariant,
    searchValue,
    virtualized
  } = useSelectContext();
  const finalVariant = variant || contextVariant;
  const searchFilter = createSearchFilter(searchValue);
  let t1;
  bb0: {
    if (!item || typeof item.label !== "string" || typeof item.value !== "string") {
      t1 = false;
      break bb0;
    }
    t1 = searchFilter(item, searchText);
  }
  const isVisible = t1;
  if (!isVisible) {
    return null;
  }
  let t2;
  if ($[9] !== className || $[10] !== finalVariant || $[11] !== virtualized) {
    t2 = cx(selectItemStyles({
      variant: finalVariant
    }), css({
      height: virtualized ? "full" : undefined,
      paddingY: virtualized ? "0" : undefined
    }), className);
    $[9] = className;
    $[10] = finalVariant;
    $[11] = virtualized;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const t3 = item.label;
  let t4;
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = css({
      display: "flex",
      flex: "auto",
      minWidth: "0"
    });
    t5 = css({
      display: "flex",
      alignItems: "center",
      gap: "12",
      width: "full"
    });
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  let t6;
  if ($[15] !== leadingIcon) {
    t6 = leadingIcon && <Icon icon={leadingIcon} size="md" decorative={true} />;
    $[15] = leadingIcon;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = css({
      display: "flex",
      alignItems: "center",
      flex: "auto",
      minWidth: "0"
    });
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== children || $[19] !== t7) {
    t8 = <span className={t7}>{children}</span>;
    $[18] = children;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== trailingIcon) {
    t9 = trailingIcon && <Icon icon={trailingIcon} size="md" decorative={true} />;
    $[21] = trailingIcon;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t5 || $[24] !== t6 || $[25] !== t8 || $[26] !== t9) {
    t10 = <div className={t5}>{t6}{t8}{t9}</div>;
    $[23] = t5;
    $[24] = t6;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10 || $[29] !== t4) {
    t11 = <ArkSelect.ItemText className={t4}>{t10}</ArkSelect.ItemText>;
    $[28] = t10;
    $[29] = t4;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  let t13;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = css({
      display: "flex",
      alignItems: "center"
    });
    t13 = <Icon icon="check" size="md" decorative={true} />;
    $[31] = t12;
    $[32] = t13;
  } else {
    t12 = $[31];
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t12) {
    t14 = <ArkSelect.ItemIndicator className={t12}>{t13}</ArkSelect.ItemIndicator>;
    $[33] = t12;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== arkUIProps || $[36] !== item || $[37] !== t11 || $[38] !== t14 || $[39] !== t2) {
    t15 = <ArkSelect.Item className={t2} item={item} role="option" aria-label={t3} {...arkUIProps}>{t11}{t14}</ArkSelect.Item>;
    $[35] = arkUIProps;
    $[36] = item;
    $[37] = t11;
    $[38] = t14;
    $[39] = t2;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  return t15;
};
export const SelectSeparator = t0 => {
  const $ = _c(5);
  const {
    className,
    variant
  } = t0;
  const {
    variant: contextVariant
  } = useSelectContext();
  const finalVariant = variant || contextVariant;
  let t1;
  if ($[0] !== className || $[1] !== finalVariant) {
    t1 = cx(selectSeparatorStyles({
      variant: finalVariant
    }), className);
    $[0] = className;
    $[1] = finalVariant;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className={t1} />;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const SelectGroup = t0 => {
  const $ = _c(16);
  let arkUIProps;
  let children;
  let className;
  let label;
  let variant;
  if ($[0] !== t0) {
    ({
      children,
      label,
      className,
      variant,
      ...arkUIProps
    } = t0);
    $[0] = t0;
    $[1] = arkUIProps;
    $[2] = children;
    $[3] = className;
    $[4] = label;
    $[5] = variant;
  } else {
    arkUIProps = $[1];
    children = $[2];
    className = $[3];
    label = $[4];
    variant = $[5];
  }
  const {
    variant: contextVariant,
    searchValue
  } = useSelectContext();
  const finalVariant = variant || contextVariant;
  const searchFilter = createSearchFilter(searchValue);
  let t1;
  bb0: {
    if (!searchValue) {
      t1 = true;
      break bb0;
    }
    const childrenArray = Children.toArray(children);
    t1 = childrenArray.some(child => {
      if (!React.isValidElement(child)) {
        return false;
      }
      if (child.type === SelectItem) {
        const itemProps = child.props as SelectItemProps;
        return searchFilter(itemProps.item, itemProps.searchText);
      }
      if (child.type === GridSelectItem) {
        const itemProps_0 = child.props as GridSelectItemProps;
        return searchFilter(itemProps_0.item, itemProps_0.searchText);
      }
      return true;
    });
  }
  const hasVisibleChildren = t1;
  if (!hasVisibleChildren) {
    return null;
  }
  let t2;
  if ($[6] !== className) {
    t2 = cx(selectGroupStyles, className);
    $[6] = className;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== finalVariant || $[9] !== label) {
    t3 = label && <ArkSelect.ItemGroupLabel className={selectGroupLabelStyles({
      variant: finalVariant
    })}>{label}</ArkSelect.ItemGroupLabel>;
    $[8] = finalVariant;
    $[9] = label;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== arkUIProps || $[12] !== children || $[13] !== t2 || $[14] !== t3) {
    t4 = <ArkSelect.ItemGroup className={t2} {...arkUIProps}>{t3}{children}</ArkSelect.ItemGroup>;
    $[11] = arkUIProps;
    $[12] = children;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
export const SelectNoResults = t0 => {
  const $ = _c(10);
  const {
    message: t1,
    className,
    variant
  } = t0;
  const message = t1 === undefined ? "No results found" : t1;
  const {
    variant: contextVariant,
    searchValue
  } = useSelectContext();
  const finalVariant = variant || contextVariant;
  if (!searchValue) {
    return null;
  }
  let t2;
  if ($[0] !== className) {
    t2 = cx(noResultsStyles, className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = finalVariant === "dark" ? "text.secondary" : "wemod.black.60";
  let t4;
  if ($[2] !== t3) {
    t4 = css({
      color: t3
    });
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== message || $[5] !== t4) {
    t5 = <span className={t4}>{message}</span>;
    $[4] = message;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t2 || $[8] !== t5) {
    t6 = <div className={t2}>{t5}</div>;
    $[7] = t2;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};